/**
 * Canvas-Frametabelle — Ersatz für den LVS_OWNERDATA-ListView aus
 * CanMonitorApp.cpp samt OnCustomDraw()/OnHeaderCustomDraw().
 *
 * Aufbau: ein Canvas zeichnet Kopfzeile und Zeilen, darüber liegt ein
 * transparenter Scroll-Container mit einem Platzhalter in voller Inhaltsgröße.
 * So übernimmt der Browser Scrollleisten, Kinetik und Tastatur, während
 * gezeichnet nur wird, was sichtbar ist (Gegenstück zu LVS_OWNERDATA +
 * LVN_GETDISPINFO).
 */

import type { RxColorMode, SignalDetailMode } from '../../app/settings';
import {
  COL,
  TABLE_COLUMN_COUNT,
  TABLE_COLUMN_TITLES,
  columnTier,
  defaultColumnWidths,
  isCellBold,
  isCellCentered,
  isHeaderCentered,
  type WidthMetrics,
} from './columns';
import {
  TablePalette,
  dotColor,
  isFading,
  rgbToCss,
  rowColor,
  type ColorSettings,
  type RowState,
} from './colors';
import type { RowKey, TableSource } from './FrameTable';

/** Untergrenze beim Ziehen einer Spaltenkante. */
const MIN_COLUMN_WIDTH = 24;
/** Fangbereich links/rechts der Spaltenkante in der Kopfzeile. */
const RESIZE_GRIP_PX = 4;
/** kDotDiameter in OnCustomDraw (Basiswert, wird skaliert). */
const DOT_DIAMETER_BASE = 13;
/** textRect.left += 6 / textRect.right -= 4 in OnCustomDraw. */
const CELL_PAD_LEFT = 6;
const CELL_PAD_RIGHT = 4;
/** Die letzte Spalte füllt den Rest; Untergrenze wie in LayoutControls(). */
const LAST_COLUMN_MIN_BASE = 80;
/** GetConsolasFont(11, true) für ID-Spalte und Kopfzeile, sonst 10pt. */
const LARGE_FONT_RATIO = 11 / 10;
/**
 * Abstand zum Listenende, bis zu dem die Ansicht wieder daran klebt. Muss
 * klein bleiben: Chrome animiert das Mausrad in ~10-px-Schritten, ein
 * Schwellwert in Zeilenhoehe haette schon den ersten Schritt als "ist ja
 * unten" gewertet und die Ansicht sofort wieder ans Ende gerissen.
 */
const TAIL_STICK_PX = 2;

/** Eine Zeile der eingeklappten Signalliste (Help > Signal Detail > List). */
export interface SignalDetailLine {
  name: string;
  /** Wert samt Einheit oder VAL_-Text, fertig formatiert. */
  value: string;
  /** Beschreibung des Signals (SPN + CM_ SG_), steht in der Description-Spalte. */
  info?: string;
}

/** Aufgeklappte Botschaft samt ihrer aktuell gezeichneten Signalzeilen. */
interface DetailBlock {
  row: number;
  lines: SignalDetailLine[];
}

export interface CanvasTableOptions {
  source: TableSource;
  /** Help > Fade Speed (0 = statisch). */
  rxFadeMs?: number;
  rxColorMode?: RxColorMode;
  /** DecodingActive(): ohne Dekodierquelle bleiben Zeilen ungefärbt. */
  decodingActive?: boolean;
  /** Gespeicherte Breiten aus den Settings; leer = Vorgaben berechnen. */
  columnWidths?: readonly number[];
  /** Doppelklick auf eine Zeile — AP-6 leitet das auf `frame:activated`. */
  onActivate?: (key: RowKey) => void;
  /** Nach dem Ziehen einer Spaltenkante, zum Persistieren. */
  onColumnWidthsChanged?: (widths: number[]) => void;
  /** Help > Signal Detail; im Modus `list` klappt der Doppelklick auf. */
  signalDetailMode?: SignalDetailMode;
  /** Liefert die Signalzeilen einer aufgeklappten Botschaft (leer = kein Aufklappen). */
  detailLines?: (key: RowKey) => SignalDetailLine[];
}

export class CanvasTable {
  readonly element: HTMLDivElement;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly scroller: HTMLDivElement;
  private readonly spacer: HTMLDivElement;
  private readonly probe: HTMLDivElement;
  private readonly palette = new TablePalette();
  private readonly resizeObserver: ResizeObserver;

  private source: TableSource;
  private readonly onActivate: ((key: RowKey) => void) | undefined;
  private readonly onColumnWidthsChanged: ((widths: number[]) => void) | undefined;
  private readonly detailLines: ((key: RowKey) => SignalDetailLine[]) | undefined;
  private signalDetailMode: SignalDetailMode = 'windows';
  /** `rowIdentity()` der aufgeklappten Botschaften. */
  private readonly expandedRows = new Set<number>();
  /** Je Zeichnung neu aufgelöst, aufsteigend nach Modellzeile. */
  private detailBlocks: DetailBlock[] = [];

  private colorSettings: ColorSettings = { rxFadeMs: 400, decodingActive: false };
  private rxColorMode: RxColorMode = 'no-colors';

  private columnWidths: number[] = [];
  /** Breite der letzten Spalte nach dem Aufziehen auf die Restbreite. */
  private readonly effectiveWidths: number[] = new Array<number>(TABLE_COLUMN_COUNT).fill(0);
  private readonly columnOffsets: number[] = new Array<number>(TABLE_COLUMN_COUNT + 1).fill(0);

  private viewportW = 0;
  private viewportH = 0;
  private rowH = 19;
  private headerH = 23;
  private fontSize = 13;
  private fontFamily = 'monospace';
  private charWidth = 8;
  private uiScale = 1;

  private selectedRow = -1;
  private followTail = true;
  private frameHandle = 0;
  private disposed = false;
  /** Erst true, wenn einmal mit echter Groesse gezeichnet wurde. */
  private hasRendered = false;

  /** Wiederverwendet, damit das Zeichnen keine Objekte je Zeile allokiert. */
  private readonly scratchState: RowState = { known: false, isError: false, lastUpdateMs: 0 };

  private resizeColumn = -1;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  constructor(options: CanvasTableOptions) {
    this.source = options.source;
    this.onActivate = options.onActivate;
    this.onColumnWidthsChanged = options.onColumnWidthsChanged;
    this.detailLines = options.detailLines;
    if (options.signalDetailMode !== undefined) this.signalDetailMode = options.signalDetailMode;
    if (options.rxFadeMs !== undefined) this.colorSettings.rxFadeMs = options.rxFadeMs;
    if (options.decodingActive !== undefined) this.colorSettings.decodingActive = options.decodingActive;
    if (options.rxColorMode !== undefined) this.rxColorMode = options.rxColorMode;

    this.element = document.createElement('div');
    this.element.className = 'cm-table';

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'cm-table__canvas';
    const ctx = this.canvas.getContext('2d', { alpha: false });
    if (ctx === null) throw new Error('CanvasTable: 2D-Kontext nicht verfügbar');
    this.ctx = ctx;

    this.scroller = document.createElement('div');
    this.scroller.className = 'cm-table__scroll';
    this.spacer = document.createElement('div');
    this.spacer.className = 'cm-table__spacer';
    this.scroller.append(this.spacer);

    this.probe = document.createElement('div');
    this.probe.className = 'cm-table__probe';

    this.element.append(this.canvas, this.scroller, this.probe);

    this.scroller.addEventListener('scroll', this.handleScroll, { passive: true });
    this.scroller.addEventListener('wheel', this.handleWheel, { passive: true });
    this.scroller.addEventListener('pointerdown', this.handleRowPointerDown);
    this.scroller.addEventListener('dblclick', this.handleRowDoubleClick);
    this.scroller.addEventListener('contextmenu', this.handleRowContextMenu);
    this.canvas.addEventListener('pointerdown', this.handleHeaderPointerDown);
    this.canvas.addEventListener('pointermove', this.handleHeaderPointerMove);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.element);

    if (options.columnWidths !== undefined && options.columnWidths.length === TABLE_COLUMN_COUNT) {
      this.columnWidths = [...options.columnWidths];
    }
  }

  // -------------------------------------------------------------------------
  // Öffentliche Steuerung
  // -------------------------------------------------------------------------

  setSource(source: TableSource): void {
    this.source = source;
    this.selectedRow = -1;
    this.collapseDetails();
    this.invalidate();
  }

  /** Help > Signal Detail. Der Wechsel klappt alles Aufgeklappte wieder zu. */
  setSignalDetailMode(mode: SignalDetailMode): void {
    if (this.signalDetailMode === mode) return;
    this.signalDetailMode = mode;
    this.collapseDetails();
  }

  /** Nach Moduswechsel oder `frames:cleared`: die Kennungen passen dann nicht mehr. */
  collapseDetails(): void {
    if (this.expandedRows.size === 0) return;
    this.expandedRows.clear();
    this.detailBlocks = [];
    this.invalidate();
  }

  /** Help-Menü: Fade Speed, Farbmodus, und ob überhaupt dekodiert wird. */
  setColorSettings(settings: Partial<ColorSettings> & { rxColorMode?: RxColorMode }): void {
    if (settings.rxFadeMs !== undefined) this.colorSettings.rxFadeMs = settings.rxFadeMs;
    if (settings.decodingActive !== undefined) {
      this.colorSettings.decodingActive = settings.decodingActive;
    }
    if (settings.rxColorMode !== undefined) this.rxColorMode = settings.rxColorMode;
    this.invalidate();
  }

  getColumnWidths(): number[] {
    return [...this.columnWidths];
  }

  setColumnWidths(widths: readonly number[]): void {
    if (widths.length !== TABLE_COLUMN_COUNT) return;
    this.columnWidths = widths.map((w) => Math.max(MIN_COLUMN_WIDTH, Math.round(w)));
    this.invalidate();
  }

  /** Maßeinheiten für defaultColumnWidths()/describeColumnWidth() des Aufrufers. */
  get widthMetrics(): WidthMetrics {
    return {
      scalePx: (basePixels: number) => Math.round(basePixels * this.uiScale),
      flatButtonWidth: (text: string) =>
        Math.ceil(text.length * this.charWidth * LARGE_FONT_RATIO) + Math.round(24 * this.uiScale),
    };
  }

  get monospaceCharWidth(): number {
    return this.charWidth;
  }

  /** Nach jedem verarbeiteten rx-batch bzw. jeder Modelländerung aufrufen. */
  invalidate(): void {
    if (this.disposed || this.frameHandle !== 0) return;
    // Die allererste Zeichnung synchron: ResizeObserver und rAF werden in
    // einem Hintergrund-Tab nicht zugestellt, die Tabelle bliebe sonst leer,
    // bis der Tab sichtbar wird.
    if (!this.hasRendered) {
      this.renderNow();
      return;
    }
    this.frameHandle = requestAnimationFrame(this.renderFrame);
  }

  /** Zeichnet sofort statt beim nächsten Animationsframe — für Messungen/Tests. */
  renderNow(): void {
    if (this.disposed) return;
    if (this.frameHandle !== 0) {
      cancelAnimationFrame(this.frameHandle);
      this.frameHandle = 0;
    }
    if (this.viewportW === 0 || this.viewportH === 0) this.readMetrics();
    this.render();
    // Ohne Groesse war das ein Leerlauf — beim naechsten Mal erneut versuchen.
    if (this.viewportW > 0 && this.viewportH > 0) this.hasRendered = true;
  }

  scrollToBottom(): void {
    this.followTail = true;
    this.scroller.scrollTop = this.scroller.scrollHeight;
  }

  /** true, solange die Ansicht am unteren Rand klebt (siehe handleScroll). */
  get isFollowingTail(): boolean {
    return this.followTail;
  }

  dispose(): void {
    this.disposed = true;
    if (this.frameHandle !== 0) cancelAnimationFrame(this.frameHandle);
    this.resizeObserver.disconnect();
    this.palette.dispose();
    this.scroller.removeEventListener('scroll', this.handleScroll);
    this.scroller.removeEventListener('wheel', this.handleWheel);
    this.scroller.removeEventListener('pointerdown', this.handleRowPointerDown);
    this.scroller.removeEventListener('dblclick', this.handleRowDoubleClick);
    this.scroller.removeEventListener('contextmenu', this.handleRowContextMenu);
    this.canvas.removeEventListener('pointerdown', this.handleHeaderPointerDown);
    this.canvas.removeEventListener('pointermove', this.handleHeaderPointerMove);
    this.element.remove();
  }

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  private handleResize(): void {
    this.readMetrics();
    // Synchron statt per rAF: das Zuweisen von canvas.width/height in
    // readMetrics() leert die Flaeche, und der ResizeObserver laeuft noch vor
    // dem Zeichnen desselben Frames -- ein spaeterer rAF-Lauf zeigte beim
    // Ziehen des Teilers je Schritt ein schwarzes Bild.
    this.renderNow();
  }

  private readMetrics(): void {
    // clientWidth/Height statt getBoundingClientRect(): Canvas und Scrollebene
    // liegen mit inset:0 in der Padding-Box, der Rahmen zählt also nicht mit.
    this.viewportW = this.element.clientWidth;
    this.viewportH = this.element.clientHeight;

    // Die Maße stehen als calc()-Ausdruck in tokens.css; getPropertyValue()
    // liefert den unaufgelösten Ausdruck. Deshalb misst ein unsichtbares
    // Probe-Element die tatsächlich aufgelösten Werte.
    const probeStyle = getComputedStyle(this.probe);
    this.rowH = Math.max(12, Math.round(this.probe.getBoundingClientRect().height));
    this.fontSize = Number.parseFloat(probeStyle.fontSize) || 13;
    this.fontFamily = probeStyle.fontFamily || 'monospace';
    this.headerH = Math.round(this.rowH * 1.2);
    this.uiScale = Number(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1;

    this.ctx.font = `${this.fontSize}px ${this.fontFamily}`;
    this.charWidth = this.ctx.measureText('0').width || this.fontSize * 0.6;

    if (this.columnWidths.length !== TABLE_COLUMN_COUNT) {
      this.columnWidths = defaultColumnWidths(this.widthMetrics);
    }

    const dpr = window.devicePixelRatio || 1;
    const backingW = Math.max(1, Math.round(this.viewportW * dpr));
    const backingH = Math.max(1, Math.round(this.viewportH * dpr));
    // Nur bei echter Größenänderung zuweisen: jede Zuweisung an width/height
    // leert den Canvas, auch wenn sich der Wert nicht ändert.
    if (this.canvas.width !== backingW || this.canvas.height !== backingH) {
      this.canvas.width = backingW;
      this.canvas.height = backingH;
      this.canvas.style.width = `${this.viewportW}px`;
      this.canvas.style.height = `${this.viewportH}px`;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.scroller.style.top = `${this.headerH}px`;
  }

  /**
   * Zieht die letzte Spalte auf die Restbreite auf, damit rechts kein
   * ungenutzter Streifen bleibt — Portierung des Aufziehens von Spalte 13
   * in LayoutControls().
   */
  private computeEffectiveWidths(): void {
    let sum = 0;
    for (let col = 0; col < TABLE_COLUMN_COUNT - 1; col += 1) {
      const width = this.columnWidths[col] ?? MIN_COLUMN_WIDTH;
      this.effectiveWidths[col] = width;
      sum += width;
    }
    const stored = this.columnWidths[TABLE_COLUMN_COUNT - 1] ?? MIN_COLUMN_WIDTH;
    const remaining = this.viewportW - sum;
    const minLast = Math.round(LAST_COLUMN_MIN_BASE * this.uiScale);
    this.effectiveWidths[TABLE_COLUMN_COUNT - 1] = Math.max(minLast, Math.max(stored, remaining));

    this.columnOffsets[0] = 0;
    for (let col = 0; col < TABLE_COLUMN_COUNT; col += 1) {
      this.columnOffsets[col + 1] = this.columnOffsets[col]! + this.effectiveWidths[col]!;
    }
  }

  private get contentWidth(): number {
    return this.columnOffsets[TABLE_COLUMN_COUNT] ?? 0;
  }

  // -------------------------------------------------------------------------
  // Zeichnen
  // -------------------------------------------------------------------------

  private readonly renderFrame = (): void => {
    this.frameHandle = 0;
    if (this.disposed) return;
    if (this.viewportW === 0 || this.viewportH === 0) this.readMetrics();
    this.render();
  };

  private render(): void {
    this.computeEffectiveWidths();

    const rowCount = this.source.rowCount();
    this.rebuildDetailBlocks(rowCount);
    const totalRows = rowCount + this.detailLineCount();
    // Noch vor dem Wachsen des Platzhalters pruefen: das Rad scrollt im
    // Compositor, das scroll-Ereignis kommt erst spaeter — ohne diese Pruefung
    // reisst der naechste Frame den Nutzer wieder ans Ende.
    if (this.followTail) {
      const distanceToBottom =
        this.scroller.scrollHeight - this.scroller.scrollTop - this.scroller.clientHeight;
      this.followTail = distanceToBottom <= TAIL_STICK_PX;
    }
    this.spacer.style.height = `${totalRows * this.rowH}px`;
    this.spacer.style.width = `${this.contentWidth}px`;
    if (this.followTail) this.scroller.scrollTop = this.scroller.scrollHeight;

    const scrollTop = this.scroller.scrollTop;
    const scrollLeft = this.scroller.scrollLeft;
    const bodyH = Math.max(0, this.viewportH - this.headerH);
    const firstRow = Math.max(0, Math.floor(scrollTop / this.rowH));
    const lastRow = Math.min(totalRows, Math.ceil((scrollTop + bodyH) / this.rowH));
    const now = performance.now();

    const ctx = this.ctx;
    ctx.fillStyle = this.palette.fieldCss;
    ctx.fillRect(0, 0, this.viewportW, this.viewportH);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    // scroller.client{Width,Height} schliesst — anders als viewportW/H, die
    // die volle .cm-table-Box messen — die eigene native Scrollleiste bereits
    // aus. Ohne diese Klammer zeichnen Kopf-/Zeileninhalt bis zum Rand der
    // vollen Box, also auch unter die (transparente) Scrollleistenspur, die
    // dann wie ein durchscheinender Zeilenrest wirkt.
    const clipW = Math.min(this.viewportW, this.scroller.clientWidth);
    const clipH = Math.min(this.viewportH, this.headerH + this.scroller.clientHeight);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, clipW, clipH);
    ctx.clip();

    let stillFading = false;
    for (let row = firstRow; row < lastRow; row += 1) {
      const y = this.headerH + row * this.rowH - scrollTop;
      const target = this.resolveVisualRow(row);
      if (target.lines === undefined) {
        if (this.drawRow(target.row, y, -scrollLeft, now)) stillFading = true;
      } else {
        this.drawDetailRow(
          target.lines[target.detail]!,
          y,
          -scrollLeft,
          target.detail === target.lines.length - 1,
        );
      }
    }

    this.drawHeader(-scrollLeft);
    ctx.restore();

    // Wie der else-Zweig in OnTimerTick(): weiterzeichnen, solange eine
    // sichtbare Zeile noch ausblendet.
    if (stillFading) this.invalidate();
  }

  // -------------------------------------------------------------------------
  // Aufgeklappte Signallisten (Help > Signal Detail > List)
  // -------------------------------------------------------------------------

  private detailLineCount(): number {
    let count = 0;
    for (const block of this.detailBlocks) count += block.lines.length;
    return count;
  }

  /**
   * Aufgeklappte Botschaften in Modellzeilen auflösen. Die Werte kommen dabei
   * frisch aus dem Modell, die Liste zeigt also mitlaufende Signalwerte.
   */
  private rebuildDetailBlocks(rowCount: number): void {
    if (this.expandedRows.size === 0) {
      if (this.detailBlocks.length > 0) this.detailBlocks = [];
      return;
    }
    const blocks: DetailBlock[] = [];
    for (let row = 0; row < rowCount; row += 1) {
      if (!this.expandedRows.has(this.source.rowIdentity(row))) continue;
      const lines = this.detailLines?.(this.source.rowKey(row)) ?? [];
      if (lines.length > 0) blocks.push({ row, lines });
    }
    this.detailBlocks = blocks;
  }

  /** Bildschirmzeile → Modellzeile; `lines` gesetzt = es ist eine Detailzeile. */
  private resolveVisualRow(visual: number): {
    row: number;
    detail: number;
    lines?: SignalDetailLine[];
  } {
    let offset = 0;
    for (const block of this.detailBlocks) {
      const start = block.row + offset;
      if (visual <= start) break;
      if (visual <= start + block.lines.length) {
        return { row: block.row, detail: visual - start - 1, lines: block.lines };
      }
      offset += block.lines.length;
    }
    return { row: visual - offset, detail: -1 };
  }

  private toggleDetail(row: number): void {
    const identity = this.source.rowIdentity(row);
    if (identity < 0) return;
    if (!this.expandedRows.delete(identity)) this.expandedRows.add(identity);
    this.invalidate();
  }

  /**
   * Eine Signalzeile: eingerückt, auf Panel-Hintergrund und mit einem
   * senkrechten Akzentstrich, damit sie sich von der Botschaft abhebt.
   */
  private drawDetailRow(line: SignalDetailLine, y: number, xOffset: number, isLast: boolean): void {
    const ctx = this.ctx;
    ctx.fillStyle = this.palette.panelCss;
    ctx.fillRect(0, y, this.viewportW, this.rowH);

    // Der Strich steht ein Stueck rechts der ID-Spalte, die Namen dahinter.
    const barX = xOffset + this.columnOffsets[COL.dlc]! + Math.round(6 * this.uiScale);
    const barWidth = Math.max(1, Math.round(2 * this.uiScale));
    ctx.fillStyle = this.palette.textMutedCss;
    ctx.fillRect(barX, y, barWidth, this.rowH);

    if (isLast) {
      ctx.fillStyle = this.palette.borderCss;
      ctx.fillRect(0, y + this.rowH - 1, this.viewportW, 1);
    }

    const nameX = barX + barWidth + Math.round(10 * this.uiScale);
    ctx.fillStyle = this.palette.textPrimaryCss;
    this.drawCellText(
      line.name,
      nameX,
      xOffset + this.columnOffsets[COL.name]! - nameX - CELL_PAD_RIGHT,
      y,
      { centered: false, bold: false },
    );

    const valueX = xOffset + this.columnOffsets[COL.name]! + CELL_PAD_LEFT;
    const valueWidth =
      this.effectiveWidths[COL.name]! + this.effectiveWidths[COL.description]! - CELL_PAD_LEFT - CELL_PAD_RIGHT;
    ctx.fillStyle = this.palette.tableAccentCss;
    this.drawCellText(line.value, valueX, valueWidth, y, { centered: false, bold: false });

    // Die Beschreibung steht in der freien Flaeche rechts von dt(ms) — dort
    // steht in einer Signalzeile ohnehin nichts.
    if (line.info !== undefined && line.info !== '') {
      const infoX = xOffset + this.columnOffsets[COL.dt]! + CELL_PAD_LEFT;
      const infoWidth = this.viewportW - infoX - CELL_PAD_RIGHT;
      if (infoWidth > 40) {
        ctx.fillStyle = this.palette.textMutedCss;
        this.drawCellText(line.info, infoX, infoWidth, y, { centered: false, bold: false });
      }
    }
  }

  private drawRow(row: number, y: number, xOffset: number, now: number): boolean {
    const ctx = this.ctx;
    const state = this.source.rowState(row, this.scratchState);
    const selected = row === this.selectedRow;

    const background = selected
      ? this.palette.tableSelectionCss
      : this.rxColorMode === 'whole-row'
        ? rgbToCss(rowColor(state, this.palette, this.colorSettings, now))
        : this.palette.fieldCss;
    ctx.fillStyle = background;
    ctx.fillRect(0, y, this.viewportW, this.rowH);

    const drawDot = this.rxColorMode === 'dots';
    if (drawDot) {
      const diameter = Math.round(DOT_DIAMETER_BASE * this.uiScale);
      const cx = xOffset + 4 + diameter / 2;
      const cy = y + this.rowH / 2;
      const backdrop = selected ? this.palette.tableSelection : this.palette.field;
      ctx.fillStyle = rgbToCss(dotColor(state, this.palette, this.colorSettings, now, backdrop));
      ctx.beginPath();
      ctx.arc(cx, cy, diameter / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    for (let col = 0; col < TABLE_COLUMN_COUNT; col += 1) {
      const left = xOffset + this.columnOffsets[col]!;
      const width = this.effectiveWidths[col]!;
      if (left + width <= 0) continue;
      if (left >= this.viewportW) break;

      const text = this.source.cell(row, col);
      if (text === '') continue;

      const padLeft = col === COL.seq && drawDot
        ? 4 + Math.round(DOT_DIAMETER_BASE * this.uiScale) + 4
        : CELL_PAD_LEFT;
      ctx.fillStyle = selected ? this.palette.textPrimaryCss : this.tierColor(columnTier(col));
      this.drawCellText(text, left + padLeft, width - padLeft - CELL_PAD_RIGHT, y, {
        centered: isCellCentered(col),
        bold: isCellBold(col) && !selected,
      });
    }

    return isFading(state, this.colorSettings, now);
  }

  private drawHeader(xOffset: number): void {
    const ctx = this.ctx;
    ctx.fillStyle = this.palette.tableHeaderCss;
    ctx.fillRect(0, 0, this.viewportW, this.headerH);

    ctx.fillStyle = this.palette.textMutedCss;
    for (let col = 0; col < TABLE_COLUMN_COUNT; col += 1) {
      const left = xOffset + this.columnOffsets[col]!;
      const width = this.effectiveWidths[col]!;
      if (left + width <= 0) continue;
      if (left >= this.viewportW) break;
      const centered = isHeaderCentered(col);
      const padLeft = centered ? 0 : CELL_PAD_LEFT;
      this.drawCellText(
        TABLE_COLUMN_TITLES[col] ?? '',
        left + padLeft,
        width - padLeft,
        0,
        { centered, bold: true },
        this.headerH,
      );
    }

    ctx.fillStyle = this.palette.borderCss;
    ctx.fillRect(0, this.headerH - 1, this.viewportW, 1);
  }

  /**
   * DT_SINGLELINE | DT_VCENTER | DT_END_ELLIPSIS. Die Schrift ist
   * dicktengleich, deshalb genügt Zeichenzählen statt measureText() je Zelle —
   * das Kürzen ist damit exakt und in O(1).
   */
  private drawCellText(
    text: string,
    x: number,
    available: number,
    y: number,
    style: { centered: boolean; bold: boolean },
    height = this.rowH,
  ): void {
    if (available <= 0) return;
    const ctx = this.ctx;
    const size = style.bold ? this.fontSize * LARGE_FONT_RATIO : this.fontSize;
    ctx.font = `${style.bold ? '600 ' : ''}${size}px ${this.fontFamily}`;
    const charWidth = style.bold ? this.charWidth * LARGE_FONT_RATIO : this.charWidth;

    const maxChars = Math.floor(available / charWidth);
    if (maxChars <= 0) return;
    const shown = text.length > maxChars ? `${text.slice(0, Math.max(0, maxChars - 1))}…` : text;
    const drawX = style.centered ? x + (available - shown.length * charWidth) / 2 : x;
    ctx.fillText(shown, Math.max(x, drawX), y + height / 2);
  }

  private tierColor(tier: ReturnType<typeof columnTier>): string {
    switch (tier) {
      case 'accent':
        return this.palette.tableAccentCss;
      case 'primary':
        return this.palette.textPrimaryCss;
      case 'dim':
        return this.palette.textDimCss;
      default:
        return this.palette.textMutedCss;
    }
  }

  // -------------------------------------------------------------------------
  // Eingaben
  // -------------------------------------------------------------------------

  private readonly handleScroll = (): void => {
    // Das Original ruft nach jedem Batch ListView_EnsureVisible() und reißt
    // den Nutzer damit beim Zurückscrollen sofort wieder ans Ende. Hier
    // bleibt die Ansicht nur am Ende kleben, solange sie dort auch steht.
    const distanceToBottom =
      this.scroller.scrollHeight - this.scroller.scrollTop - this.scroller.clientHeight;
    this.followTail = distanceToBottom <= TAIL_STICK_PX;
    this.invalidate();
  };

  /** Das scroll-Ereignis kommt zu spaet: schon die Radgeste loest das Kleben. */
  private readonly handleWheel = (event: WheelEvent): void => {
    if (event.deltaY < 0) this.followTail = false;
  };

  private rowAt(event: PointerEvent | MouseEvent): number {
    const rect = this.scroller.getBoundingClientRect();
    const y = event.clientY - rect.top + this.scroller.scrollTop;
    const row = Math.floor(y / this.rowH);
    return row >= 0 && row < this.source.rowCount() + this.detailLineCount() ? row : -1;
  }

  private readonly handleRowPointerDown = (event: PointerEvent): void => {
    const row = this.rowAt(event);
    if (row < 0) return;
    this.selectedRow = this.resolveVisualRow(row).row;
    this.invalidate();
  };

  private readonly handleRowDoubleClick = (event: MouseEvent): void => {
    this.activateRowAt(event);
  };

  /** Rechtsklick oeffnet/schliesst die Signalliste genauso wie der Doppelklick. */
  private readonly handleRowContextMenu = (event: MouseEvent): void => {
    if (this.activateRowAt(event)) event.preventDefault();
  };

  private activateRowAt(event: MouseEvent): boolean {
    const row = this.rowAt(event);
    if (row < 0) return false;
    const target = this.resolveVisualRow(row);
    // Doppelklick in die Liste selbst klappt sie wieder zu.
    if (target.lines !== undefined || this.signalDetailMode === 'list') {
      this.toggleDetail(target.row);
      return true;
    }
    if (this.onActivate === undefined) return false;
    this.onActivate(this.source.rowKey(target.row));
    return true;
  }

  /** Spaltenkante unter `x` (Rand zwischen col und col+1), sonst -1. */
  private boundaryAt(x: number): number {
    const local = x + this.scroller.scrollLeft;
    for (let col = 0; col < TABLE_COLUMN_COUNT; col += 1) {
      if (Math.abs(local - this.columnOffsets[col + 1]!) <= RESIZE_GRIP_PX) return col;
    }
    return -1;
  }

  private readonly handleHeaderPointerMove = (event: PointerEvent): void => {
    if (event.offsetY >= this.headerH) return;
    this.canvas.style.cursor = this.boundaryAt(event.offsetX) >= 0 ? 'col-resize' : 'default';
  };

  private readonly handleHeaderPointerDown = (event: PointerEvent): void => {
    if (event.offsetY >= this.headerH) return;
    const col = this.boundaryAt(event.offsetX);
    if (col < 0) return;
    event.preventDefault();
    this.resizeColumn = col;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.effectiveWidths[col] ?? MIN_COLUMN_WIDTH;
    window.addEventListener('pointermove', this.handleResizeDrag);
    window.addEventListener('pointerup', this.handleResizeEnd, { once: true });
  };

  private readonly handleResizeDrag = (event: PointerEvent): void => {
    if (this.resizeColumn < 0) return;
    const width = Math.max(
      MIN_COLUMN_WIDTH,
      Math.round(this.resizeStartWidth + (event.clientX - this.resizeStartX)),
    );
    this.columnWidths[this.resizeColumn] = width;
    this.invalidate();
  };

  private readonly handleResizeEnd = (): void => {
    window.removeEventListener('pointermove', this.handleResizeDrag);
    this.resizeColumn = -1;
    this.onColumnWidthsChanged?.(this.getColumnWidths());
  };
}
